import api from '@/lib/api/axios'

export interface DisplayDevice {
  id: number
  name: string
  location: string | null
  layout: Array<{ path: string; title?: string }>
  createdAt: string
  expiresAt: string | null
  lastSeenAt: string | null
  revokedAt: string | null
  status: 'active' | 'revoked' | 'expired'
}

/** Returned only from create — the token is shown once and never again. */
export interface CreatedDisplay extends DisplayDevice {
  token: string
  tokenShownOnce: true
}

const unwrap = <T,>(res: { data: unknown }): T => {
  const body = res.data as { data?: T } | T
  return ((body as { data?: T })?.data ?? body) as T
}

export const displaysApi = {
  list: () => api.get('/api/displays').then(unwrap<DisplayDevice[]>),

  create: (body: { name: string; location?: string; layout?: Array<{ path: string; title?: string }>; expiryDays?: number }) =>
    api.post('/api/displays', body).then(unwrap<CreatedDisplay>),

  update: (id: number, body: Partial<Pick<DisplayDevice, 'name' | 'location' | 'layout'>>) =>
    api.put(`/api/displays/${id}`, body).then(unwrap<DisplayDevice>),

  revoke: (id: number) => api.delete(`/api/displays/${id}`).then(unwrap<{ id: number; revoked: boolean }>),

  /**
   * Start a display session.
   *
   * Pass the token ONLY on first enrolment. After that the browser holds it as an httpOnly
   * cookie this code cannot read, so every later call sends no token at all — including the
   * one after an unattended reboot.
   *
   * Deliberately NOT using the shared axios instance: that one attaches whatever access
   * token happens to be in the auth store and runs the 401-refresh interceptor, and this
   * call must be judged purely on the display credential the browser presents.
   */
  startSession: async (token?: string) => {
    const res = await fetch('/api/displays/session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // same-origin, so the path-scoped display cookie rides along. Explicit rather than
      // relying on the default, because this call only works if it does.
      credentials: 'same-origin',
      body: JSON.stringify(token ? { token } : {}),
    })
    if (!res.ok) throw new Error('This display is not authorised')
    const body = await res.json()
    return (body?.data ?? body) as { accessToken: string; display: DisplayDevice }
  },
}
